import { useEffect } from "react";
import { useQuery, useQueryClient, type QueryKey, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import type { StoreName } from "../storage/db.js";
import { readCatalogCache, writeCatalogCache, type CatalogCacheEntry } from "../storage/catalogCache.js";
import { runsOnDevice } from "../api/client.js";
import { catalogRefreshAfterMs } from "./syncSettingsStore.js";

/**
 * Device-seeded query (technical-spec §6.2): on a cold mount (no data in the
 * in-memory React Query cache yet), seed the query from the IndexedDB catalog
 * cache so the page paints instantly, then invalidate to trigger a background
 * refresh. Every successful network fetch write-throughs, so the device copy
 * is always the newest successful upload.
 *
 * In-session navigation is untouched: once React Query holds data, the seed
 * is skipped (memory is fresher than disk) and normal staleTime rules apply.
 */
interface DeviceSeededOptions<T> extends Omit<UseQueryOptions<T, Error, T, QueryKey>, "queryKey" | "queryFn" | "enabled" | "select"> {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  cacheStore: StoreName;
  cacheKey: string;
  sizeOf: (data: T) => number;
  enabled?: boolean;
}

export function useDeviceSeededQuery<T, TSelected = T>(
  options: DeviceSeededOptions<T> & { select?: (data: T) => TSelected },
): UseQueryResult<TSelected, Error> {
  const { queryKey, queryFn, cacheStore, cacheKey, sizeOf, enabled = true, select, staleTime = 1000 * 60 * 10, ...rest } = options;
  const queryClient = useQueryClient();
  const keyJson = JSON.stringify(queryKey);

  // Seed the query cache from IndexedDB on mount when the in-memory cache is
  // cold, then mark it stale so the network refresh runs in the background.
  useEffect(() => {
    if (!enabled) return;
    // Memory first. This read used to happen on EVERY mount and be thrown away
    // once it found the query already filled — which, after the first screen,
    // it always is: every visit to Home, the guide or Movies decoded the whole
    // stored list (10k channels, 40k+ titles) for nothing. Profiled on the
    // 1 GB projector as ~0.34 s of the Back from the guide to Home (2026-09-24).
    if (queryClient.getQueryData(JSON.parse(keyJson) as QueryKey) !== undefined) return;
    let cancelled = false;
    void readCatalogCache<T>(cacheStore, cacheKey).then((entry) => {
      if (cancelled || !entry) return;
      const parsedKey = JSON.parse(keyJson) as QueryKey;
      // Only seed when there's no data yet — a warm session has fresher data.
      if (queryClient.getQueryData(parsedKey) === undefined) {
        queryClient.setQueryData(parsedKey, entry.data);
        if (!storedCopyIsCurrent(entry.savedAt)) void queryClient.invalidateQueries({ queryKey: parsedKey });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, cacheStore, cacheKey, keyJson, queryClient]);

  return useQuery({
    queryKey,
    queryFn: async () => {
      const data = await queryFn();
      void writeCatalogCache(cacheStore, cacheKey, data, sizeOf);
      return data;
    },
    staleTime,
    enabled,
    select,
    ...rest,
  } as UseQueryOptions<T, Error, TSelected, QueryKey>);
}

/**
 * In the APK, is the stored copy young enough to stand as it is?
 *
 * Refreshing it is not a download there — the device answers from its own
 * store, and downloads only once that is past the viewer's sync frequency —
 * but it IS a rebuild on the page's thread: every row re-hashed and redacted,
 * the whole list turned to JSON and back, then written to disk again. On the
 * 1 GB projector that was most of the first 20 s of every cold start, for a
 * copy saved minutes earlier and the very lists it already showed
 * (2026-09-26). So within the sync frequency a launch keeps what it has;
 * past it, or with nothing stored, it refreshes as before. The website keeps
 * refreshing on every load: its server has the newer data, and a desktop has
 * the headroom.
 */
export function storedCopyIsCurrent(savedAt: string | undefined, now = Date.now()): boolean {
  if (!runsOnDevice() || !savedAt) return false;
  const age = now - Date.parse(savedAt);
  return Number.isFinite(age) && age >= 0 && age < catalogRefreshAfterMs();
}

export type { CatalogCacheEntry };
